import {
  useCallback, useEffect, useMemo, useRef, useState,
  type CSSProperties, type PointerEvent as ReactPointerEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAllowedApps } from "../hooks/useAllowedApps";
import { useAuth } from "../hooks/useAuth";
import {
  LAUNCHER_APPS, useLauncherBadges, useLauncherController, type LauncherApp,
} from "../components/home-launcher";
import { GlyphFlowBackground, type GlyphFlowHandle } from "../components/home-launcher/GlyphFlowBackground";
import { OrbitAtmosphere } from "../components/home-launcher/OrbitAtmosphere";
import { INSTANCE } from "../lib/instance";
import { brandWord } from "../../../shared/instance";
import "../styles/home-launcher.css";
import "../styles/motion-plus.css";
import "../styles/glyph-flow-refined.css";

const HOME_KICKER = `${brandWord(INSTANCE).toUpperCase()} / HOME`;
const HOME_TRANSITION_MS = 520;
const HUB_ICON = INSTANCE.logoUrl;

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(
    "button, a, input, select, textarea, summary, dialog, [role='dialog'], [contenteditable], [data-glyph-exclude]",
  ));
}

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { allowed, loading } = useAllowedApps();
  const rootRef = useRef<HTMLElement>(null);
  const wheelCenterRef = useRef<HTMLDivElement>(null);
  const glyphRef = useRef<GlyphFlowHandle>(null);
  const launchTimerRef = useRef<number | null>(null);
  const launchingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const suppressClickTimerRef = useRef<number | null>(null);
  const activeWheelPointerRef = useRef<number | null>(null);
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const [userPaused, setUserPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [motionPaused, setMotionPaused] = useState(prefersReducedMotion || document.hidden);

  const sortedApps = useMemo(() => LAUNCHER_APPS.filter((app) => {
    const appKey = app.shellApp?.appKey ?? null;
    return appKey == null || (allowed != null && allowed.has(appKey));
  }), [allowed]);
  const { getBadge } = useLauncherBadges();
  const wheel = useLauncherController(wheelCenterRef, sortedApps.length, motionPaused);
  const focusedApp = wheel.focusedIdx >= 0 ? sortedApps[wheel.focusedIdx] : sortedApps[0];

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    media.addEventListener("change", update);
    update();
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => () => {
    if (launchTimerRef.current != null) window.clearTimeout(launchTimerRef.current);
    if (suppressClickTimerRef.current != null) window.clearTimeout(suppressClickTimerRef.current);
  }, []);

  const suppressDragClick = () => {
    suppressClickRef.current = true;
    if (suppressClickTimerRef.current != null) window.clearTimeout(suppressClickTimerRef.current);
    suppressClickTimerRef.current = window.setTimeout(() => {
      suppressClickRef.current = false;
      suppressClickTimerRef.current = null;
    }, 0);
  };

  const cancelPointer = () => {
    glyphRef.current?.pointerCancel();
    if (activeWheelPointerRef.current != null) {
      activeWheelPointerRef.current = null;
      wheel.end();
      suppressDragClick();
    }
  };
  useEffect(() => {
    window.addEventListener("blur", cancelPointer);
    return () => window.removeEventListener("blur", cancelPointer);
  }, [wheel]);

  const pointForGlyph = (event: ReactPointerEvent<HTMLElement>) => {
    const box = rootRef.current?.getBoundingClientRect();
    return {
      pointerId: event.pointerId, pointerType: event.pointerType,
      x: event.clientX - (box?.left ?? 0), y: event.clientY - (box?.top ?? 0),
    };
  };

  const launchApp = (app: LauncherApp) => {
    if (launchingRef.current) return;
    if (app.shellApp?.kind === "external" && app.shellApp.externalUrl) {
      window.open(app.shellApp.externalUrl, "_blank", "noopener");
      return;
    }
    launchingRef.current = true;
    setLaunchingId(app.id);
    launchTimerRef.current = window.setTimeout(() => navigate(app.route), motionPaused ? 0 : HOME_TRANSITION_MS);
  };

  // One React pointer owner for the entire Home surface. The renderer never
  // captures events; free space outside the wheel is a genuine glyph tap too.
  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (!event.isPrimary || event.button !== 0 || launchingRef.current || isInteractiveTarget(event.target)) return;
    suppressClickRef.current = false;
    glyphRef.current?.pointerDown(pointForGlyph(event));
    if (sortedApps.length > 1 && event.target instanceof Element && event.target.closest(".home-wheel-stage")) {
      activeWheelPointerRef.current = event.pointerId;
      wheel.begin(event.clientX, event.clientY);
    }
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* best effort */ }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!isInteractiveTarget(event.target) || activeWheelPointerRef.current === event.pointerId) {
      glyphRef.current?.pointerMove(pointForGlyph(event));
    }
    if (activeWheelPointerRef.current === event.pointerId) wheel.move(event.clientX, event.clientY);
  };

  const releasePointer = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* best effort */ }
    }
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    // Pointer capture retargets pointerup to Home. Still exclude the actual UI
    // beneath the release coordinate and never turn a drag into a glyph tap.
    const releasedOnControl = isInteractiveTarget(document.elementFromPoint(event.clientX, event.clientY));
    let moved = false;
    if (activeWheelPointerRef.current === event.pointerId) {
      activeWheelPointerRef.current = null; // clear BEFORE lostpointercapture
      moved = wheel.end().moved;
      if (moved) suppressDragClick();
    }
    if (releasedOnControl || moved) glyphRef.current?.pointerCancel(event.pointerId);
    else glyphRef.current?.pointerUp(pointForGlyph(event));
    releasePointer(event);
  };

  const handlePointerCancel = (event: ReactPointerEvent<HTMLElement>) => {
    glyphRef.current?.pointerCancel(event.pointerId);
    if (activeWheelPointerRef.current === event.pointerId) {
      activeWheelPointerRef.current = null;
      wheel.end();
      suppressDragClick();
    }
    releasePointer(event);
  };

  const handleAppClick = (app: LauncherApp) => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    launchApp(app);
  };
  const handleMotionChange = useCallback((paused: boolean) => setMotionPaused(paused), []);
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? "";

  if (loading || allowed == null) return (
    <section className="home-launcher home-launcher--loading" aria-label="Hub Home wird geladen">
      <img src={HUB_ICON} alt="" className="home-loading-logo" />
      <div className="home-loading-line" /><span>Apps werden geladen</span>
    </section>
  );
  if (sortedApps.length === 0) return (
    <section className="home-launcher home-launcher--empty" aria-labelledby="home-empty-title">
      <img src={HUB_ICON} alt="" className="home-empty-logo" />
      <p className="home-kicker">{HOME_KICKER}</p><h1 id="home-empty-title">Keine Apps freigegeben</h1>
      <p>Für Ihr Konto ist derzeit keine Anwendung hinterlegt.</p>
    </section>
  );

  const step = 360 / sortedApps.length;
  const extraApps = Math.max(0, sortedApps.length - 9);
  const radiusPercent = sortedApps.length >= 13 ? 44 : sortedApps.length >= 10 ? 43 : 41.5;
  const stageStyle = {
    "--home-app-count": sortedApps.length,
    "--home-app-size": `${Math.max(64, 72 - extraApps * 1.5)}px`,
    "--home-app-size-mobile": `${Math.max(42, 48 - extraApps * 1.6)}px`,
    "--home-app-label-width": `${Math.max(64, 112 - extraApps * 3)}px`,
    "--home-app-label-width-mobile": `${Math.max(56, 68 - extraApps * 2.5)}px`,
  } as CSSProperties;

  return (
    <section ref={rootRef}
      className={`home-launcher${launchingId ? " is-launching" : ""}${motionPaused ? " home-launcher--motion-paused" : ""}`}
      data-testid="hub-home" data-launching-app={launchingId ?? undefined} aria-labelledby="home-title"
      onPointerDown={handlePointerDown} onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp} onPointerCancel={handlePointerCancel} onLostPointerCapture={handlePointerCancel}
    >
      <GlyphFlowBackground ref={glyphRef} userPaused={userPaused} reducedMotion={prefersReducedMotion} onMotionChange={handleMotionChange} />
      <OrbitAtmosphere />
      <div className="home-orbit-glow" aria-hidden="true" />
      {!prefersReducedMotion ? (
        <button type="button" className="home-motion-toggle" aria-pressed={userPaused}
          aria-label={userPaused ? "Bewegung fortsetzen" : "Bewegung anhalten"}
          onClick={() => setUserPaused((paused) => !paused)} data-testid="home-motion-toggle">
          <span aria-hidden="true">{userPaused ? "▶" : "Ⅱ"}</span>
          <span>{userPaused ? "Bewegung fortsetzen" : "Bewegung anhalten"}</span>
        </button>
      ) : null}
      <div className="home-wheel-stage" data-testid="home-app-wheel" style={stageStyle}>
        <div className="motion-plus" aria-hidden="true">
          <span className="motion-plus-comet" />
          <span className="motion-plus-ripple motion-plus-ripple--one" />
          <span className="motion-plus-ripple motion-plus-ripple--two" />
          <span className="motion-plus-ripple motion-plus-ripple--three" />
          <span className="motion-plus-radar" />
          <span className="motion-plus-packet motion-plus-packet--one" />
          <span className="motion-plus-packet motion-plus-packet--two" />
          <span className="motion-plus-packet motion-plus-packet--three" />
          <span className="motion-plus-packet motion-plus-packet--four" />
          <span className="motion-plus-packet motion-plus-packet--five" />
        </div>
        <div className="home-wheel-track" aria-hidden="true">
          <span className="home-orbit-light-trail home-orbit-light-trail--outer" />
          <span className="home-orbit-light-trail home-orbit-light-trail--inner" />
          <span className="home-wheel-track-inner" /><span className="home-wheel-focus-marker" />
        </div>
        <div ref={wheelCenterRef} className="home-wheel-center">
          <img src={HUB_ICON} alt="" className="home-wheel-logo" />
          <p className="home-kicker">{HOME_KICKER}</p>
          <h1 id="home-title">{firstName ? `Willkommen, ${firstName}` : "Willkommen"}</h1>
          <p className="home-wheel-instruction">Wählen Sie eine Anwendung</p>
          {focusedApp ? (
            <button type="button" className="home-wheel-selection home-wheel-selection--action"
              style={{ "--home-selection-color": focusedApp.color } as CSSProperties}
              aria-label={`${focusedApp.name} öffnen`} onClick={() => handleAppClick(focusedApp)} data-testid="home-selection-launch">
              <span>{focusedApp.name}</span>
              <small><span>{String(wheel.focusedIdx + 1).padStart(2, "0")} / {String(sortedApps.length).padStart(2, "0")}</span><b>Öffnen</b></small>
            </button>
          ) : null}
          <span className="home-wheel-hint">Klicken oder Kreis drehen</span>
        </div>
        {sortedApps.map((app, index) => {
          const radians = (-90 + index * step - wheel.rotation) * Math.PI / 180;
          const focused = index === wheel.focusedIdx;
          const launching = launchingId === app.id;
          const badge = getBadge(app.id);
          const style = {
            "--home-app-left": `${50 + radiusPercent * Math.cos(radians)}%`,
            "--home-app-top": `${50 + radiusPercent * Math.sin(radians)}%`,
            "--home-app-color": app.color, "--home-app-delay": `${Math.min(index * 38, 380)}ms`,
            "--home-app-float-delay": `${(index % 5) * -0.72}s`,
            "--home-app-float-duration": `${4.8 + (index % 4) * 0.55}s`,
          } as CSSProperties;
          return (
            <div key={app.id} className={`home-wheel-position${launching ? " is-selected" : ""}`} style={style} data-app-id={app.id}>
              <button type="button" className={`home-wheel-app${focused ? " is-focused" : ""}${launching ? " is-selected" : ""}`}
                onClick={() => handleAppClick(app)} aria-label={`${app.name} öffnen`}
                aria-current={focused ? "true" : undefined} data-testid={`home-app-${app.id}`}>
                <span className="home-wheel-icon-shell"><span className="home-wheel-icon-wrap">
                  <img src={app.icon} alt="" draggable={false} />
                  {badge > 0 ? <span className="home-wheel-badge" aria-label={`${badge} neue Hinweise`}>{badge > 99 ? "99+" : badge}</span> : null}
                </span></span>
                <span className="home-wheel-app-label">{app.name}</span>
              </button>
            </div>
          );
        })}
      </div>
      <div className="home-launch-status" aria-live="polite">
        {launchingId ? `${sortedApps.find((app) => app.id === launchingId)?.name ?? "App"} wird geöffnet` : `${sortedApps.length} Apps verfügbar`}
      </div>
    </section>
  );
}
